import { Injectable, InjectionToken, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';

import { LoadableStore } from '@nx-vigil/core/web/store/data-access';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

export const CORE_SERVICE = new InjectionToken<CoreService>('CoreService');
export interface CoreService {
  getStatus(): Observable<EnvironmentState>;
}

export const CORE_ENVIRONMENT_DEFAULTS = new InjectionToken<EnvironmentState>(
  'CoreEnvironmentDefaults',
);

export interface EnvironmentState {
  readonly version?: string;
  readonly deploymentVersion?: string;
  readonly outOfSyncFromDeployment?: boolean;
  readonly features?: { [key: string]: boolean };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly configuration?: { [key: string]: any };
}

@Injectable({ providedIn: 'root' })
export class EnvironmentStore extends LoadableStore<EnvironmentState> {
  private readonly defaults =
    inject<EnvironmentState>(CORE_ENVIRONMENT_DEFAULTS, { optional: true }) ??
    {};
  private readonly core = inject<CoreService>(CORE_SERVICE);

  public constructor() {
    super({});

    this.load();
  }

  public readonly version$ = this.onceLoaded$.pipe(
    switchMap(() => this.select(({ version }) => version)),
  );

  public readonly features$ = this.onceLoaded$.pipe(
    switchMap(() => this.select(({ features }) => features ?? {})),
  );
  public getFeature$(key: string) {
    return this.features$.pipe(map(features => features[key] ?? false));
  }

  public readonly configuration$ = this.onceLoaded$.pipe(
    switchMap(() => this.select(({ configuration }) => configuration ?? {})),
  );

  public getConfiguration$(key: string) {
    return this.configuration$.pipe(map(configuration => configuration[key]));
  }

  public refresh(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.load({ resolve, reject });
    });
  }

  protected readonly load = this.effect<{
    resolve: () => void;
    reject: (error: Error) => void;
  } | void>(event$ =>
    event$.pipe(
      tap(() => {
        this.setLoading();
      }),
      switchMap(completion =>
        this.core.getStatus().pipe(
          tapResponse({
            next: status => {
              this.patchState({
                ...this.defaults,
                ...status,
                features: {
                  ...this.defaults.features,
                  ...status.features,
                },
              });
              this.setLoaded();
              completion?.resolve();
            },
            error: (error: Error) => {
              this.setError(
                $localize`:@@alert-error-load-environment:Failed to load environment`,
                error,
              );
              completion?.reject(error);
            },
          }),
        ),
      ),
    ),
  );
}
